import { useState } from 'react';
import type { ReactNode } from 'react';

/**
 * A settings row that says yes or no — TROOP-191.
 *
 * SPEC-GAP, on the same terms as {@link LinkRow} and `Tabs`: `handoff/design-system.md` §6 lists
 * Button, Chip, Card, FactRow, ParticipantChip, ComplianceNotice, AnnouncementCard, MessageBubble,
 * EmptyState — and no switch, toggle or checkbox row of any kind. Per §10 ("prefer the nearest
 * specified pattern") this is built from `tokens.css` only: `--text-fact` and `--text-caption` as
 * `LinkRow` uses them, `--color-text-primary` as the ink exactly as `Tabs` uses it for the selected
 * underline, `--color-border-strong` for the resting track, `--tap-min` for the target, and the
 * global `:focus-visible` ring. No new colour, size or radius.
 *
 * **A row, never a bare switch.** Every use this product has is a row in a settings list, and a
 * loose `<Switch>` is one that ends up inside a form where "on" reads ambiguously against the
 * fields around it. The label is part of the control rather than beside it, so the whole 44px row
 * is the hit target — which is the difference between a toggle that works on a phone and one that
 * needs a careful thumb.
 *
 * **State is carried by knob position and `aria-checked`, never by colour alone** — the reasoning
 * §8 gives for the event palette and `Tabs` repeats for the selected tab. Desaturate this and the
 * knob is still left or right.
 *
 * **No `disabled` prop, only `disabledReason`** (`Button`'s rule, and CLAUDE.md's). §20 leans on
 * it directly: an `Interrupt`-tier notification cannot be declined, so that row must say *who*
 * decided rather than grey a switch and leave the reader to guess. Like `Button`, this is
 * `aria-disabled` rather than the `disabled` attribute, so a screen-reader user can still reach
 * the control to discover why it is unavailable.
 *
 * ## The optimistic write, decided here so no screen decides it again
 *
 * It flips **immediately** and reverts if the write fails. A toggle that waits for a round trip
 * reads as broken on the connection a family actually has, and the failure here is recoverable and
 * worth showing rather than hiding behind a spinner.
 *
 * What stops that becoming TROOP-167's bug — a draft state that quietly disagrees with what is on
 * screen — is that **the optimistic value is a short-lived override and never a second source of
 * truth**. This component is controlled: `checked` is the prop, `pending` is set only while a
 * write is in flight, and it is cleared in a `finally` whatever happens. So the row always settles
 * back onto the prop.
 *
 * The consequence is deliberate and worth knowing: if a caller's `onChange` resolves without
 * updating `checked`, the row **snaps back**. That is correct. A row left showing a state the
 * server does not have is the silent failure; a visible snap-back is a bug somebody reports.
 */
export function ToggleRow({
  label,
  secondary,
  checked,
  onChange,
  disabledReason,
  failureMessage = "That didn't save. Try again.",
}: {
  /** The setting, as a short phrase. Sentence case, never all-caps. */
  label: string;
  /** What turning it on actually does, in a sentence. */
  secondary?: ReactNode;
  checked: boolean;
  /**
   * Called with the value being asked for. May return a promise; while it is pending the row
   * shows the new value and refuses further input. A rejection reverts the row and shows
   * {@link failureMessage}.
   */
  onChange: (next: boolean) => void | Promise<void>;
  /**
   * Why this cannot be changed. Its presence is what disables the row — there is no `disabled`
   * prop, because a greyed control with no explanation is the support cost §6 names.
   */
  disabledReason?: string;
  failureMessage?: string;
}) {
  const [pending, setPending] = useState<boolean | null>(null);
  const [failed, setFailed] = useState(false);

  const locked = Boolean(disabledReason);
  const busy = pending !== null;

  // The prop, except while a write is in flight. Cleared in `finally`, so this can never become a
  // second answer to what the setting is.
  const shown = pending ?? checked;

  async function flip() {
    if (locked || busy) return;

    const next = !checked;
    setPending(next);
    setFailed(false);

    try {
      await onChange(next);
    } catch {
      setFailed(true);
    } finally {
      setPending(null);
    }
  }

  return (
    <div style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
      <button
        type="button"
        role="switch"
        aria-checked={shown}
        aria-disabled={locked || undefined}
        onClick={() => void flip()}
        className="grid items-center w-full text-left"
        style={{
          // Not FactRow's 100px label column, deliberately: that column holds about nine
          // characters at 12px uppercase, and these labels are phrases ("Event changes", "What
          // your family owes") rather than the one-word categories a docket row carries. The text
          // takes the slack and the switch keeps its own width at the right edge, which is the
          // arrangement CLAUDE.md's note on `justify-between` says to reach for.
          gridTemplateColumns: '1fr auto',
          gap: 'var(--space-4)',
          minHeight: 'var(--tap-min)',
          padding: 'var(--space-3) 0',
          background: 'none',
          border: 'none',
          cursor: locked ? 'default' : 'pointer',
          opacity: locked ? 0.6 : 1,
        }}
      >
        <span style={{ minWidth: 0 }}>
          <span className="t-fact" style={{ color: 'var(--color-text-primary)', display: 'block' }}>
            {label}
          </span>
          {secondary && (
            <span
              className="t-caption"
              style={{ color: 'var(--color-text-secondary)', display: 'block' }}
            >
              {secondary}
            </span>
          )}
        </span>

        {/* The switch itself. Presentational -- the button above carries role and state, so this
            must not announce itself a second time. */}
        <span
          aria-hidden="true"
          style={{
            position: 'relative',
            flexShrink: 0,
            width: '44px',
            height: '26px',
            borderRadius: 'var(--radius-full)',
            background: shown ? 'var(--color-text-primary)' : 'var(--color-surface-page)',
            border: `1px solid ${shown ? 'var(--color-text-primary)' : 'var(--color-border-strong)'}`,
            transition: 'background-color 120ms',
          }}
          className="motion-reduce:transition-none"
        >
          <span
            style={{
              position: 'absolute',
              top: '3px',
              left: shown ? '21px' : '3px',
              width: '18px',
              height: '18px',
              borderRadius: 'var(--radius-full)',
              background: shown ? 'var(--color-surface-page)' : 'var(--color-border-strong)',
              transition: 'left 120ms',
            }}
            className="motion-reduce:transition-none"
          />
        </span>
      </button>

      {(disabledReason || failed) && (
        <p
          className="t-caption"
          style={{
            color: failed ? 'var(--color-status-blocked)' : 'var(--color-text-muted)',
            margin: '0 0 var(--space-3)',
          }}
        >
          {failed ? failureMessage : disabledReason}
        </p>
      )}
    </div>
  );
}
